import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Card } from '@/components/ui/card';
import { Lightbox } from '@/components/site/Lightbox';
import { publicApi } from '@/lib/api';
import { absoluteImageUrl } from '@/components/admin/ImageUploader';
import { ENTRY_TYPES, formatDate, labelFor, youtubeEmbedUrl } from '@/lib/taxonomies';
import { useSeo } from '@/lib/seo';
import { SEASON } from '@/constants/testIds';

export default function SeasonDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    publicApi
      .seasonDetail(slug)
      .then(setData)
      .catch(() => setNotFound(true));
  }, [slug]);

  const entry = data?.entry;
  useSeo({
    title: entry ? `${entry.title} | The Season | A Guy Who Hunts` : 'The Season | A Guy Who Hunts',
    description: entry?.summary,
    ogImage: entry?.hero_image?.url,
    type: 'article',
  });

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-20 text-center">
        <p className="kicker">404</p>
        <h1 className="font-serif text-4xl mt-2">Entry not found.</h1>
        <p className="mt-3 text-[hsl(var(--muted-foreground))]">
          The season entry you’re looking for isn’t here.
        </p>
        <Link to="/season" className="mt-6 inline-block underline">
          Back to The Season
        </Link>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="h-6 w-40 bg-[hsl(var(--muted))] animate-pulse rounded" />
        <div className="mt-6 h-10 w-3/4 bg-[hsl(var(--muted))] animate-pulse rounded" />
        <div className="mt-6 aspect-[16/9] w-full bg-[hsl(var(--muted))] animate-pulse rounded-2xl" />
      </div>
    );
  }

  const embed = entry.youtube_url ? youtubeEmbedUrl(entry.youtube_url) : null;
  const { prev, next } = data || {};

  return (
    <article>
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pt-8">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/season">The Season</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="line-clamp-1">{entry.title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <header className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 mt-6">
        <div className="flex flex-wrap items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
          <Badge variant="secondary" className="font-normal">
            {labelFor(ENTRY_TYPES, entry.entry_type)}
          </Badge>
          <span>•</span>
          <time>{formatDate(entry.date)}</time>
          {entry.weather && (
            <>
              <span>•</span>
              <span>{entry.weather}</span>
            </>
          )}
          {entry.wind && (
            <>
              <span>•</span>
              <span>Wind: {entry.wind}</span>
            </>
          )}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight tracking-tight mt-3">{entry.title}</h1>
        {entry.summary && (
          <p className="mt-4 text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">{entry.summary}</p>
        )}
      </header>

      {entry.hero_image?.url && (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 mt-8">
          <Card className="overflow-hidden border-[hsl(var(--border))] card-elev">
            <AspectRatio ratio={16 / 9}>
              <img
                src={absoluteImageUrl(entry.hero_image.url)}
                alt={entry.hero_image.alt || entry.title}
                className="h-full w-full object-cover"
              />
            </AspectRatio>
          </Card>
        </div>
      )}

      <div className="mx-auto max-w-[72ch] px-4 sm:px-6 lg:px-8 py-12 prose-editorial">
        {entry.body?.split(/\n\n+/).map((para, i) => <p key={i}>{para}</p>)}
      </div>

      {embed && (
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pb-14">
          <Card className="overflow-hidden border-[hsl(var(--border))] card-elev">
            <AspectRatio ratio={16 / 9}>
              <iframe
                src={embed}
                title={entry.title}
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </AspectRatio>
          </Card>
        </div>
      )}

      {entry.gallery?.length > 0 && (
        <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pb-14">
          <div className="mb-5 flex items-baseline justify-between gap-4">
            <p className="kicker">Gallery</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              {entry.gallery.length} image{entry.gallery.length === 1 ? '' : 's'}
            </p>
          </div>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {entry.gallery.map((img, i) => (
              <figure
                key={`${img.url}-${i}`}
                className="rounded-2xl overflow-hidden border border-[hsl(var(--border))] bg-[hsl(var(--card))]"
              >
                <button
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
                  aria-label={`Open image ${i + 1} of ${entry.gallery.length} in full-screen`}
                  data-testid="season-gallery-image"
                >
                  <AspectRatio ratio={4 / 3}>
                    <img
                      src={absoluteImageUrl(img.url)}
                      alt={img.alt || `Photo ${i + 1} — ${entry.title}`}
                      className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.02]"
                      loading="lazy"
                    />
                  </AspectRatio>
                </button>
                {img.alt && (
                  <figcaption className="px-3 py-2 text-xs text-[hsl(var(--muted-foreground))] line-clamp-2">
                    {img.alt}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
          {lightboxIndex !== null && (
            <Lightbox
              images={entry.gallery}
              index={lightboxIndex}
              onIndexChange={setLightboxIndex}
              onClose={() => setLightboxIndex(null)}
            />
          )}
        </section>
      )}

      <nav className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pb-20 grid gap-4 sm:grid-cols-2">
        {prev ? (
          <Link
            to={`/season/${prev.slug}`}
            data-testid={SEASON.prev}
            className="group rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 transition-shadow card-elev-hover"
          >
            <span className="kicker inline-flex items-center gap-1">
              <ChevronLeft size={12} /> Previous entry
            </span>
            <p className="font-serif text-lg mt-2 tracking-tight group-hover:underline">{prev.title}</p>
          </Link>
        ) : (
          <div />
        )}
        {next && (
          <Link
            to={`/season/${next.slug}`}
            data-testid={SEASON.next}
            className="group rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 text-right transition-shadow card-elev-hover"
          >
            <span className="kicker inline-flex items-center gap-1">
              Next entry <ChevronRight size={12} />
            </span>
            <p className="font-serif text-lg mt-2 tracking-tight group-hover:underline">{next.title}</p>
          </Link>
        )}
      </nav>
    </article>
  );
}
